// FE Polish Phase 8 — PII redaction for Sentry breadcrumbs + events.
//
// The backend audit log is the source of truth for who-did-what; Sentry should
// only know what broke. To honor that:
//   - Strip email-shaped values from URL paths/queries
//   - Strip JWT/bearer-token-shaped values
//   - Strip values from any query param named after a PII field
//   - Strip request bodies entirely (breadcrumb-level)
//
// Wired into Sentry as `beforeBreadcrumb` and `beforeSend`.

const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g
// JWT: three base64url segments separated by dots, minimum lengths so we don't
// mangle ordinary tokens. Generous matching; better to over-redact.
const JWT_RE = /\beyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\b/g
// Long token-shaped opaque blobs (32+ url-safe chars in a row).
const BEARER_RE = /\b(?:Bearer|token=|access_token=|refresh_token=)\s*[A-Za-z0-9._~+/=-]{20,}/gi
// Phone shapes — broad pattern, intentionally aggressive.
const PHONE_RE = /\+?\d{1,3}[\s.-]?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}/g
// US SSN-shaped numbers.
const SSN_RE = /\b\d{3}-\d{2}-\d{4}\b/g
// Credit-card-shaped runs of 13–19 digits, optionally space/dash-separated.
const CARD_RE = /\b(?:\d[ -]?){13,19}\b/g

const PII_PARAM_NAMES = new Set([
  'email', 'e_mail', 'username', 'phone', 'phone_number', 'ssn', 'tax_id',
  'password', 'pwd', 'token', 'access_token', 'refresh_token', 'api_key',
  'authorization', 'auth', 'credit_card', 'card_number', 'cvv',
])

function redactString(value: string): string {
  if (!value) return value
  return value
    .replace(JWT_RE, '[REDACTED_JWT]')
    .replace(BEARER_RE, '[REDACTED_TOKEN]')
    .replace(EMAIL_RE, '[REDACTED_EMAIL]')
    .replace(SSN_RE, '[REDACTED_SSN]')
    .replace(CARD_RE, '[REDACTED_CARD]')
    .replace(PHONE_RE, '[REDACTED_PHONE]')
}

function redactUrl(url: string | undefined): string | undefined {
  if (!url) return url
  try {
    const parsed = new URL(url, 'http://x.invalid')
    parsed.searchParams.forEach((val, key) => {
      if (PII_PARAM_NAMES.has(key.toLowerCase())) {
        parsed.searchParams.set(key, '[REDACTED]')
      } else {
        parsed.searchParams.set(key, redactString(val))
      }
    })
    const out = parsed.toString()
    if (out.startsWith('http://x.invalid')) return redactString(out.slice('http://x.invalid'.length))
    return redactString(out)
  } catch {
    return redactString(url)
  }
}

/**
 * Sentry `beforeBreadcrumb` hook — runs on every breadcrumb (including auto-captured
 * fetch/xhr/console breadcrumbs). Returns `null` to drop a breadcrumb entirely.
 */
export function redactBreadcrumb(breadcrumb: any): any | null {
  if (!breadcrumb) return null

  if (breadcrumb.message) {
    breadcrumb.message = redactString(String(breadcrumb.message))
  }

  if (breadcrumb.data) {
    if (typeof breadcrumb.data.url === 'string') {
      breadcrumb.data.url = redactUrl(breadcrumb.data.url)
    }
    if (typeof breadcrumb.data.request_url === 'string') {
      breadcrumb.data.request_url = redactUrl(breadcrumb.data.request_url)
    }
    // Drop request/response bodies entirely — too easy to leak PII.
    delete breadcrumb.data.body
    delete breadcrumb.data.request_body
    delete breadcrumb.data.response_body
  }

  return breadcrumb
}

/**
 * Sentry `beforeSend` hook — runs on every event before transport. Redacts
 * request URL, message, exception values, and removes user.email if present.
 */
export function redactEvent(event: any): any | null {
  if (!event) return null

  if (event.message) {
    event.message = redactString(String(event.message))
  }

  if (event.request) {
    if (typeof event.request.url === 'string') {
      event.request.url = redactUrl(event.request.url)
    }
    // Body data may contain form fields — drop entirely.
    delete event.request.data
    delete event.request.cookies
    if (event.request.headers) {
      delete event.request.headers.Authorization
      delete event.request.headers.authorization
      delete event.request.headers.Cookie
      delete event.request.headers.cookie
    }
  }

  if (event.user) {
    delete event.user.email
    delete event.user.ip_address
    delete event.user.username
  }

  if (event.exception?.values) {
    event.exception.values = event.exception.values.map((ex: any) => {
      if (ex && typeof ex.value === 'string') {
        ex.value = redactString(ex.value)
      }
      return ex
    })
  }

  if (Array.isArray(event.breadcrumbs)) {
    event.breadcrumbs = event.breadcrumbs.map(redactBreadcrumb).filter(Boolean)
  }

  return event
}

// Exported for tests.
export const __test__ = { redactString, redactUrl }
